import { useEffect } from "react";
import TextField, { TextAreaField } from "@/components/common/text-field";
import SelectField from "@/components/common/select";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  FileItem,
  Heading,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import { useAssignTagToProcess, useDuplicateProcess } from "@/queries/process";
import { useGetTags } from "@/queries/tags/tags.api";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { newProcessSchema } from "@/schemas/processes";
import type { AxiosError } from "axios";
import type { ProcessType, StoreProcessData } from "@/types/entities/process";
import {
  uploadFile,
  useUnassignTagToProcess,
} from "@/queries/process/process.api";
import ScrollModal from "@/components/common/scroll-modal";
import { DialogTitle } from "@/components/ui/dialog";
import { useFileUpload } from "@/pages/procesos/components/NewProcessModal";
import { getEnv } from "@/utils/runtime-config";

interface DuplicateProcessModalProps {
  isOpen?: boolean;
  modalData?: ProcessType | undefined;
  onClose?: () => void;
  processId: string | undefined;
  onSuccess?: () => void;
}

const MAX_FILE_SIZE_MB = Number(getEnv("VITE_MAX_FILE_SIZE_MB") || 20); // 20 MB por defecto
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const DuplicateProcessModal = ({
  isOpen = false,
  modalData = undefined,
  onClose = () => {},
  processId = "",
  onSuccess = () => {},
}: DuplicateProcessModalProps) => {
  const { mutateAsync, isPending: isLoading } = useDuplicateProcess();
  const { mutateAsync: assignTagMutation, isPending: isAssigningTag } =
    useAssignTagToProcess();
  const { mutateAsync: unassignTagMutation, isPending: isUnassigningTag } =
    useUnassignTagToProcess();
  const { data: tagsData, isLoading: isLoadingTags } = useGetTags({
    order_by: "!updated_at",
    per_page: 1000,
    relationships: "files",
  });
  const { showToast } = useToast();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    watch,
    reset,
    setError,
    trigger,
  } = useForm({
    resolver: zodResolver(newProcessSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      name: `${modalData?.name || ""} (Copia)`,
      description: modalData?.description || "",
      tag_id: (() => {
        if (modalData?.tags?.[0]) {
          return modalData?.tags?.[0].active
            ? modalData?.tags?.[0]?.id || ""
            : "";
        } else {
          return "";
        }
      })(),
    },
  });
  const [description, tagId] = watch(["description", "tag_id"]);

  useEffect(() => {
    reset({
      name: `${modalData?.name || ""} (Copia)`,
      description: modalData?.description || "",
      tag_id: modalData?.tags?.[0]?.id || "",
    });
  }, [isOpen, modalData, reset]);

  const handleSave = async (data: StoreProcessData) => {
    try {
      const duplicatedData = await mutateAsync({
        id: processId,
        payload: {
          name: data.name,
          description: data.description,
        },
      });
      if (files.length > 0) {
        await Promise.all(
          files.map((file) =>
            uploadFile({
              file,
              entity_id: duplicatedData.data.id,
              entity_type: "process",
            }),
          ),
        );
      }

      // Asignar tag si se seleccionó una diferente
      if (data.tag_id) {
        //to clean exisintg multiple tags;
        if (modalData?.tags && (modalData?.tags?.length || 0) > 1) {
          await Promise.all(
            modalData?.tags.map((tag) =>
              unassignTagMutation({
                tag_id: tag.id,
                entity: "process",
                entity_id: duplicatedData.data.id,
              }),
            ),
          );
        }

        // Solo actualizar tags si realmente cambió la etiqueta
        if (modalData?.tags?.[0]?.id !== data.tag_id) {
          if (modalData?.tags?.[0]?.id) {
            await unassignTagMutation({
              tag_id: modalData?.tags?.[0]?.id || "",
              entity: "process",
              entity_id: duplicatedData.data.id,
            });
          }

          await assignTagMutation({
            tag_id: data.tag_id,
            entity: "process",
            entity_id: duplicatedData.data.id,
          });
        }
      }

      onSuccess();
      onClose();
      showToast({
        title: "Proceso duplicado",
        description: "El proceso ha sido duplicado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch (e) {
      const error = e as AxiosError<{
        message?: string;
        errors?: { [key: string]: string[] };
      }>;
      // Manejo de error por nombre duplicado
      const apiMessage = error?.response?.data?.message?.toLowerCase?.() ?? "";
      const nameErrorMsg =
        error?.response?.data?.errors?.name?.[0]?.toLowerCase?.() ?? "";

      console.log({ apiMessage });

      if (
        apiMessage.includes("validation.unique") ||
        nameErrorMsg.includes("validation.unique") ||
        apiMessage.includes("name has already been taken") ||
        nameErrorMsg.includes("name has already been taken") ||
        apiMessage.includes("el name ya ha sido tomado.")
      ) {
        setError("name", {
          type: "duplicate",
          message: "El nombre ya está en uso. Usa uno diferente.",
        });
      } else {
        setError("name", {
          type: "server",
          message:
            error?.message ||
            error?.response?.data?.message ||
            "Error al actualizar el proceso",
        });
      }
    }
  };

  const {
    files,
    invalidFiles,
    isDragging,
    setIsDragging,
    handleFileChange,
    handleDrop,
    handleRemoveFile,
  } = useFileUpload(MAX_FILE_SIZE_BYTES, (files) => {
    setValue("files", files);
  });

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] h-full flex flex-col gap-0"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <DialogTitle>Duplicar Proceso</DialogTitle>
          <Button
            type="button"
            onClick={onClose}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" />
          </Button>
        </div>
      }
      footer={
        <div className="grid grid-cols-2 gap-2 py-3 px-8">
          <Button
            type="button"
            variant="tertiary"
            className="w-full font-semibold border"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form="duplicate-process-form"
            className="w-full font-semibold"
            disabled={
              isLoading || !isValid || isAssigningTag || isUnassigningTag
            }
          >
            Crear proceso
          </Button>
        </div>
      }
    >
      <div className="p-modal-md flex flex-col gap-3">
        <form
          className="flex flex-col flex-1 min-h-0 gap-6"
          onSubmit={handleSubmit(handleSave)}
          id="duplicate-process-form"
        >
          <Heading variant="h6">Detalles</Heading>
          <TextField
            label="Nombre"
            placeholder="Nombre de proceso"
            required
            {...register("name")}
            error={errors.name?.message || ""}
          />

          <SelectField
            name="tag_id"
            label="Etiqueta"
            required
            optionTextClassName="[&>*]:block [&>*]:w-full"
            placeholder="Elegir una etiqueta"
            error={errors.tag_id?.message || ""}
            value={tagId || ""}
            onChange={(value) => {
              setValue("tag_id", value);
              setTimeout(() => {
                trigger("tag_id");
              }, 0);
            }}
            isLoading={isLoadingTags}
            options={(() => {
              const options = tagsData?.data.map((tag) => ({
                label: tag.name,
                value: tag.id.toString(),
                avatar: (
                  <Avatar size="2xs" shape="square">
                    <AvatarImage src={tag?.files?.[0]?.url} />
                    <AvatarFallback color="neutral" shape="square">
                      <Icon name="tag" type="fas" />
                    </AvatarFallback>
                  </Avatar>
                ),
                iconPosition: "right",
                icon: (
                  <Badge
                    size="sm"
                    shape="pill"
                    variant={tag.active ? "success" : "neutral"}
                  >
                    {tag.active ? "Activa" : "Inactiva"}
                  </Badge>
                ),
              }));
              return options || [];
            })()}
          />

          <TextAreaField
            label="Descripción"
            placeholder="Descripción del proceso"
            required
            limit={500}
            maxLength={500}
            className="min-h-[88px]"
            count={description?.length || 0}
            {...register("description")}
            error={errors.description?.message || ""}
          />
        </form>
        <div className="w-full h-px my-7 bg-[var(--border-neutral-silent)]" />
        <>
          <Heading variant="h6">Ficha técnica</Heading>
          <div
            className={`border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center gap-2 transition-colors duration-200 ${
              isDragging
                ? "border-neutral bg-surface-action-accent-quiet"
                : "border-neutral"
            }`}
            style={{ minHeight: 120 }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
            }}
            onDrop={handleDrop}
          >
            {isDragging ? (
              <div className="flex flex-col items-center justify-center gap-2">
                <Avatar shape="square">
                  <AvatarFallback color="action" shape="square">
                    <Icon
                      type="far"
                      name="arrow-up"
                      className="text-base text-action-quiet mb-1 w-3"
                    />
                  </AvatarFallback>
                </Avatar>
                <span className="text-action-quiet font-medium">
                  Suelta tu archivo aquí
                </span>
              </div>
            ) : (
              <>
                <span className="text-body text-sm">
                  Arrastra y Suelta o{" "}
                  <label className="text-action cursor-pointer font-medium">
                    Selecciona un archivo
                    <input
                      type="file"
                      accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.zip"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                </span>
                <span className="text-xs text-body-quiet">
                  Sube uno o varios archivos (.pdf, .docx, .xlsx, .png, .jpg,
                  .jpeg, .zip)
                </span>
              </>
            )}
            {errors.files?.message && (
              <span className="text-danger text-xs">
                {errors.files?.message || ""}
              </span>
            )}
          </div>
        </>
        {(files.length > 0 || invalidFiles.length > 0) && (
          <div className="mt-4 flex flex-col gap-3">
            {files.map((file, index) => (
              <div
                key={`${file.name}-valid-file-${index}`}
                className="flex flex-col gap-1.5"
              >
                <label
                  className={"font-semibold text-body-loud text-sm leading-2xl"}
                >
                  Ficha técnica
                </label>
                <FileItem
                  fileName={file.name}
                  tag={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                  color="neutral"
                  action={<Icon type="far" name="trash" size="sm" />}
                  actionAriaLabel="Borrar"
                  onAction={() => {
                    handleRemoveFile(file.name);
                  }}
                />
              </div>
            ))}
            {invalidFiles
              .filter((f) => !files.some((file) => file.name === f.name))
              .map((file) => (
                <div className="flex flex-col gap-1.5" key={file.name}>
                  <FileItem
                    key={file.name}
                    fileName={file.name}
                    tag={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                    color="danger"
                    action={<Icon type="far" name="trash" size="sm" />}
                    actionAriaLabel="Borrar"
                    onAction={() => handleRemoveFile(file.name)}
                  />
                  <span className="text-xs text-danger mt-0 mb-2 flex items-center gap-1 pl-2">
                    <Icon
                      type="far"
                      name="circle-xmark"
                      className="text-danger text-sm"
                    />
                    Archivo muy pesado, no se incluirá en el proceso.
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>
    </ScrollModal>
  );
};

export default DuplicateProcessModal;
